import type { FC, ReactElement } from 'react';

import { useCms, useContent } from '@eneco/cms';
import RichText from '@eneco/components/RichText/RichText';
import { NotificationBox, Table as SparkyTable, Text } from '@eneco/sparky';

import type { TableCell, TableContent, TableRow } from './table-content.types';
import { tableRows } from './table-rows';

/**
 * `Table` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Sitecore sends the rows grouped by its Table contents resolver; Sanity stores them nested in `items`,
 * which `tableRows` groups the same way. A block without rows renders its title, plus a dev-only notice.
 */
const Table: FC = () => {
  const content = useContent<TableContent>();
  const { headerRows, rows, sections, footerRows } = tableRows(content);

  const hasRows = Boolean(
    headerRows?.length ||
      rows?.length ||
      footerRows?.length ||
      sections?.some(section => section.rows?.length || section.footerRows?.length),
  );

  if (!hasRows) {
    return (
      <>
        {content.title && <Text weight="bold">{content.title}</Text>}
        {process.env.NODE_ENV !== 'production' && (
          <NotificationBox
            isAlert={false}
            title="Table rows not migrated"
            text="This block has no rows."
          />
        )}
      </>
    );
  }

  return (
    <SparkyTable title={content.title}>
      {headerRows?.[0] && <SparkyTable.Header>{cells(headerRows[0])}</SparkyTable.Header>}

      {rows?.map((row, index) => <SparkyTable.Row key={index}>{cells(row)}</SparkyTable.Row>)}

      {sections?.map((section, index) =>
        section.rows?.length ? (
          <SparkyTable.Section key={index} isCollapsible={section.collapsible} title={section.title ?? ''}>
            {section.rows.map((row, rowIndex) => (
              <SparkyTable.Row key={rowIndex}>{cells(row)}</SparkyTable.Row>
            ))}
            {section.footerRows?.[0] && <SparkyTable.Row isFooter>{cells(section.footerRows[0])}</SparkyTable.Row>}
          </SparkyTable.Section>
        ) : null,
      )}

      {footerRows?.[0] && <SparkyTable.Row isFooter>{cells(footerRows[0])}</SparkyTable.Row>}
    </SparkyTable>
  );
};

/** A row's cells. Sparky's row/header/section children are typed as element arrays, so this returns
 * one rather than a `ReactNode`. */
const cells = (row: TableRow): ReactElement[] =>
  (row.columns ?? []).map((column, index) => <TableCellText key={index} cell={column} />);

const TableCellText: FC<{ cell: TableCell }> = ({ cell }) => {
  const { adapter } = useCms();
  const html = adapter.toRichText(cell.content);

  return <Text>{html ? <RichText html={html} /> : null}</Text>;
};

export default Table;
